import { useEffect } from "react";
import { X, Check } from "lucide-react";

const ORIENTATIONS = [
  { key: "", label: "All orientations" },
  { key: "landscape", label: "Horizontal" },
  { key: "portrait", label: "Vertical" },
  { key: "square", label: "Square" },
];

const COLORS = [
  { key: "", label: "Any", hex: null },
  { key: "red", label: "Red", hex: "#c0392b" },
  { key: "orange", label: "Orange", hex: "#e67e22" },
  { key: "yellow", label: "Yellow", hex: "#f1c40f" },
  { key: "green", label: "Green", hex: "#27ae60" },
  { key: "turquoise", label: "Turquoise", hex: "#1abc9c" },
  { key: "blue", label: "Blue", hex: "#2c74b3" },
  { key: "violet", label: "Violet", hex: "#8e44ad" },
  { key: "pink", label: "Pink", hex: "#e75480" },
  { key: "brown", label: "Brown", hex: "#7b4f2c" },
  { key: "black", label: "Black", hex: "#111111" },
  { key: "gray", label: "Gray", hex: "#7f8c8d" },
  { key: "white", label: "White", hex: "#f5f2ea" },
];

export default function FiltersPanel({ open, orientation, color, onChange, onClose, onClearAll }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        onClick={onClose}
        data-testid="filters-backdrop"
        className={`fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="filters-panel"
        role="dialog"
        aria-modal="true"
        className={`fixed top-0 left-0 z-50 h-full w-full sm:w-[380px] bg-ivory shadow-2xl transform transition-transform duration-400 ease-[cubic-bezier(0.22,0.61,0.36,1)] ${
          open ? "translate-x-0" : "-translate-x-full"
        } flex flex-col`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Refine</div>
            <div className="font-serif text-xl text-ink">Filters</div>
          </div>
          <button
            onClick={onClose}
            data-testid="filters-close"
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-secondary transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          <section>
            <div className="text-[11px] uppercase tracking-[0.3em] text-ink mb-3">Orientation</div>
            <div className="space-y-1">
              {ORIENTATIONS.map((o) => {
                const active = orientation === o.key;
                return (
                  <button
                    key={o.key || "all"}
                    onClick={() => onChange({ orientation: o.key })}
                    data-testid={`filter-orientation-${o.key || "all"}`}
                    className={`w-full flex items-center justify-between px-3 py-2.5 border transition-colors text-sm ${
                      active ? "border-gold bg-gold text-ivory" : "border-border bg-white text-ink hover:border-ink"
                    }`}
                  >
                    <span>{o.label}</span>
                    {active && <Check className="h-4 w-4 text-gold" />}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-[0.3em] text-ink mb-3">Color</div>
            <div className="grid grid-cols-7 gap-2">
              {COLORS.map((c) => {
                const active = color === c.key;
                return (
                  <button
                    key={c.key || "any"}
                    onClick={() => onChange({ color: c.key })}
                    data-testid={`filter-color-${c.key || "any"}`}
                    title={c.label}
                    className={`h-9 w-9 rounded-full border transition-all ${
                      active ? "ring-2 ring-offset-1 ring-gold border-ink" : "border-border hover:scale-110"
                    }`}
                    style={{
                      background: c.hex || "conic-gradient(#c0392b,#f1c40f,#27ae60,#2c74b3,#8e44ad,#c0392b)",
                    }}
                  >
                    {!c.key && <span className="sr-only">Any color</span>}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <div className="border-t border-border p-4 flex gap-3 shrink-0">
          <button
            onClick={onClearAll}
            data-testid="filters-clear-all"
            className="flex-1 px-4 py-2.5 border border-border text-sm hover:border-ink"
          >
            Clear all
          </button>
          <button
            onClick={onClose}
            data-testid="filters-apply"
            className="flex-1 px-4 py-2.5 bg-gold text-ivory text-sm hover:bg-gold/90"
          >
            Show results
          </button>
        </div>
      </aside>
    </>
  );
}
